/**
 * Telegram Dialogs Page — Separate view for all Telegram conversations
 * with order-like status tracking and flow step visibility.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { telegramApi, TelegramDialog, TelegramStats, TelegramDialogFilters } from '../api/telegram';
import { formatRelativeTime } from '../utils/formatters';

// ── Status labels & colors ─────────────────────────────────────────────

const ORDER_STATUS_LABELS: Record<string, string> = {
  NEW: 'Нове',
  PAYMENT_PENDING: 'Очікує оплати',
  PAYMENT_CONFIRMED: 'Оплачено',
  INFO_COLLECTING: 'Збір інфо',
  LYRICS_GENERATING: 'Генерація тексту',
  LYRICS_REVIEW: 'Текст на перевірці',
  LYRICS_APPROVED: 'Текст затверджено',
  SUNO_GENERATING: 'Запис музики',
  QUALITY_CHECK: 'Контроль якості',
  DELIVERY: 'Доставка',
  COMPLETED: 'Завершено',
  CANCELLED: 'Скасовано',
  ERROR: 'Помилка',
};

const ORDER_STATUS_COLORS: Record<string, string> = {
  NEW: '#1677ff',
  PAYMENT_PENDING: '#faad14',
  PAYMENT_CONFIRMED: '#52c41a',
  INFO_COLLECTING: '#1677ff',
  LYRICS_GENERATING: '#722ed1',
  LYRICS_REVIEW: '#eb2f96',
  LYRICS_APPROVED: '#52c41a',
  SUNO_GENERATING: '#722ed1',
  QUALITY_CHECK: '#fa8c16',
  DELIVERY: '#13c2c2',
  COMPLETED: '#52c41a',
  CANCELLED: '#ff4d4f',
  ERROR: '#ff4d4f',
};

const FLOW_STEP_LABELS: Record<string, string> = {
  occasion: 'Нагода',
  mood: 'Настрій',
  recipient: "Ім'я",
  details: 'Деталі',
  confirm: 'Підтвердження',
};

// ── Components ──────────────────────────────────────────────────────────

function StatusBadge({ status, type }: { status: string; type: 'order' | 'flow' | 'none' }) {
  if (type === 'flow') {
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        background: '#e6f4ff',
        color: '#1677ff',
        border: '1px solid #91caff',
      }}>
        {FLOW_STEP_LABELS[status] || status}
      </span>
    );
  }

  if (type === 'none') {
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        background: '#f5f5f5',
        color: '#8c8c8c',
        border: '1px solid #d9d9d9',
      }}>
        Без замовлення
      </span>
    );
  }

  const color = ORDER_STATUS_COLORS[status] || '#8c8c8c';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {ORDER_STATUS_LABELS[status] || status}
    </span>
  );
}

function StatCard({ title, value, color }: { title: string; value: string | number; color?: string }) {
  return (
    <div style={{
      flex: 1,
      padding: '16px 20px',
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #f0f0f0',
      minWidth: 140,
    }}>
      <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: color || '#1a1a1a' }}>{value}</div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────

export default function TelegramDialogs() {
  const navigate = useNavigate();
  const [dialogs, setDialogs] = useState<TelegramDialog[]>([]);
  const [stats, setStats] = useState<TelegramStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0, pages: 0 });

  // Filters
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [hasOrderFilter, setHasOrderFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const loadDialogs = useCallback(async () => {
    try {
      setLoading(true);
      const params: TelegramDialogFilters = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (orderStatusFilter) params.order_status = orderStatusFilter;
      if (hasOrderFilter === 'yes') params.has_order = true;
      if (hasOrderFilter === 'no') params.has_order = false;
      if (searchFilter) params.search = searchFilter;

      const response = await telegramApi.getDialogs(params);
      setDialogs(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total,
        pages: response.pages,
      }));
    } catch (err) {
      console.error('Failed to load telegram dialogs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, orderStatusFilter, hasOrderFilter, searchFilter]);

  const loadStats = useCallback(async () => {
    try {
      const data = await telegramApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load telegram stats:', err);
    }
  }, []);

  useEffect(() => {
    loadDialogs();
    loadStats();
  }, [loadDialogs, loadStats]);

  const handleRowClick = (dialog: TelegramDialog) => {
    navigate(`/conversations/${dialog.conversation_id}`);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>
          Telegram
        </h1>
        <p style={{ color: '#8c8c8c', margin: '4px 0 0' }}>
          Всі діалоги з клієнтами через Telegram
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard title="Всього діалогів" value={stats.total_dialogs} />
          <StatCard title="Активні потоки" value={stats.active_flows} color="#1677ff" />
          <StatCard title="З замовленнями" value={stats.dialogs_with_orders} color="#52c41a" />
          <StatCard title="Конверсія" value={`${stats.conversion_rate}%`} color="#722ed1" />
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <select
          value={orderStatusFilter}
          onChange={(e) => {
            setOrderStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            fontSize: 14,
            background: '#fff',
          }}
        >
          <option value="">Всі статуси</option>
          {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={hasOrderFilter}
          onChange={(e) => {
            setHasOrderFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            fontSize: 14,
            background: '#fff',
          }}
        >
          <option value="">Всі діалоги</option>
          <option value="yes">З замовленням</option>
          <option value="no">Без замовлення</option>
        </select>

        <input
          type="text"
          placeholder="Пошук за ім'ям або ID..."
          value={searchFilter}
          onChange={(e) => {
            setSearchFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            fontSize: 14,
            minWidth: 220,
          }}
        />

        <button
          onClick={() => { loadDialogs(); loadStats(); }}
          style={{
            padding: '6px 16px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Оновити
        </button>
      </div>

      {/* Table */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #f0f0f0',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
              <th style={thStyle}>Клієнт</th>
              <th style={thStyle}>Username</th>
              <th style={thStyle}>Замовлення</th>
              <th style={thStyle}>Статус</th>
              <th style={thStyle}>Потік</th>
              <th style={thStyle}>Повідомлень</th>
              <th style={thStyle}>Останнє</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                  Завантаження...
                </td>
              </tr>
            ) : dialogs.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                  Діалогів не знайдено
                </td>
              </tr>
            ) : (
              dialogs.map((dialog) => (
                <tr
                  key={dialog.conversation_id}
                  onClick={() => handleRowClick(dialog)}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{dialog.client_name || 'Невідомий'}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{dialog.channel_user_id}</div>
                  </td>
                  <td style={tdStyle}>
                    {dialog.username ? (
                      <span style={{ color: '#1677ff' }}>@{dialog.username}</span>
                    ) : (
                      <span style={{ color: '#d9d9d9' }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {dialog.order_id ? (
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        background: '#f5f5f5',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}>
                        {dialog.order_id}
                      </span>
                    ) : (
                      <span style={{ color: '#d9d9d9' }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {dialog.order_status ? (
                      <StatusBadge status={dialog.order_status} type="order" />
                    ) : dialog.flow_step ? (
                      <StatusBadge status={dialog.flow_step} type="flow" />
                    ) : (
                      <StatusBadge status="" type="none" />
                    )}
                  </td>
                  <td style={tdStyle}>
                    {dialog.flow_step ? (
                      <StatusBadge status={dialog.flow_step} type="flow" />
                    ) : (
                      <span style={{ color: '#d9d9d9' }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      background: '#f0f0f0',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 12,
                    }}>
                      {dialog.message_count}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {dialog.last_message_at ? (
                      <span style={{ fontSize: 13, color: '#595959' }}>
                        {formatRelativeTime(dialog.last_message_at)}
                      </span>
                    ) : (
                      <span style={{ color: '#d9d9d9' }}>—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 8,
          marginTop: 16,
        }}>
          <button
            disabled={pagination.page <= 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            style={paginationBtnStyle}
          >
            ←
          </button>
          <span style={{ padding: '6px 12px', fontSize: 14 }}>
            {pagination.page} / {pagination.pages}
          </span>
          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            style={paginationBtnStyle}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 13,
  color: '#595959',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
};

const paginationBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid #d9d9d9',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};
